import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, ReactNode } from 'react';
import { Backdrop, Box, Dialog, styled, SxProps, Typography } from '@mui/material';
import { CloseIcon } from 'components/icons/CloseIcon';

export interface ModalProps {
  open: boolean;
  title?: string;
  showCloseIcon?: boolean;
  onClose?: () => void;
  children: ReactNode;
  backgroundColor?: string;
  headerStyle?: React.CSSProperties;
  contentStyle?: SxProps;
  paperSx?: SxProps;
}

export const Modal = ({
  open,
  title,
  showCloseIcon = true,
  onClose,
  children,
  backgroundColor = '#ffffff',
  headerStyle,
  contentStyle,
  paperSx,
}: ModalProps): ReactElement => {
  const backdropComponent = styled(Backdrop, {
    name: 'MuiModal',
    slot: 'Backdrop',
    overridesResolver: (props, styles) => {
      return styles.backdrop;
    },
  })({
    bottom: 0,
  });

  const defaultPaperStyles = {
    position: { xs: 'absolute', md: 'relative' },
    bottom: { xs: 0, md: 'unset' },
    margin: { xs: 'unset', md: 32 },
    borderRadius: 20,
    borderBottomLeftRadius: { xs: 0, md: 20 },
    borderBottomRightRadius: { xs: 0, md: 20 },
    width: { xs: '100%', md: 'unset' },
    left: 0,
    right: 0,
    background: `${backgroundColor} !important`,
    boxShadow:
      '0px 11px 15px -7px rgb(0 0 0 / 20%), 0px 24px 38px 3px rgb(0 0 0 / 14%), 0px 9px 46px 8px rgb(0 0 0 /12%',
  };

  return (
    <Dialog
      BackdropComponent={backdropComponent}
      open={open}
      sx={{
        boxShadow: '0 3px 30px #233a47a7',
        height: '100%',
        transition: 'none !important',
        '& .MuiBackdrop-root': {
          position: 'fixed',
          transition: 'none !important',
        },
        '& .MuiDialog-container': {
          transition: 'none !important',
        },
        '& .MuiPaper-root': {
          ...defaultPaperStyles,
          ...(paperSx as Record<string, unknown>),
        },
      }}
      maxWidth={false}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown')
          onClose?.();
      }}
      keepMounted={false}
    >
      <Box
        padding={showCloseIcon ? '24px 24px 0 24px' : 'unset'}
        sx={{
          color: theme => theme.palette.text.primary,
        }}
      >
        {onClose && showCloseIcon && (
          <Box
            fontSize={pxToEm(20)}
            lineHeight={0}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              cursor: 'pointer',
            }}
            onClick={onClose}
          >
            <CloseIcon />
          </Box>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            flex={1}
            display="flex"
            justifyContent={{ xs: 'unset', md: 'center' }}
            style={headerStyle}
          >
            <Typography
              style={{
                fontWeight: 600,
                fontSize: pxToEm(24),
                color: '#000',
              }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ ...contentStyle }} p={24} pb={30}>
        {children}
      </Box>
    </Dialog>
  );
};
